import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FilePicker,
  Grid,
  Input,
  Label,
  RequiredMark,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  Textarea,
} from '@shared/ui';

type DemoFormValues = {
  name: string;
  email: string;
  role: string;
  notes: string;
};

export function FormExampleShowcase() {
  const { t } = useTranslation();
  const [submittedName, setSubmittedName] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const form = useForm<DemoFormValues>({
    defaultValues: {
      name: '',
      email: '',
      role: 'student',
      notes: '',
    },
  });

  const onSubmit = (values: DemoFormValues) => {
    setSubmittedName(values.name);
  };
  const handleSubmit = form.handleSubmit(onSubmit);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('uiLibrary.forms.example.title')}</CardTitle>
        <CardDescription>{t('uiLibrary.forms.example.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            noValidate
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            className="space-y-4"
          >
            <Grid cols={1} gap="md" className="md:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                rules={{
                  required: t('uiLibrary.forms.validation.name'),
                  minLength: {
                    value: 2,
                    message: t('uiLibrary.forms.validation.name'),
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('uiLibrary.forms.fields.name.label')}
                      <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        required
                        aria-required="true"
                        placeholder={t('uiLibrary.forms.fields.name.placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('uiLibrary.forms.fields.name.helper')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                rules={{
                  required: t('uiLibrary.forms.validation.email'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/u,
                    message: t('uiLibrary.forms.validation.email'),
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('uiLibrary.forms.fields.email.label')}
                      <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        required
                        aria-required="true"
                        type="email"
                        placeholder={t('uiLibrary.forms.fields.email.placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('uiLibrary.forms.fields.email.helper')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Grid>

            <FormField
              control={form.control}
              name="role"
              rules={{
                required: t('uiLibrary.forms.validation.role'),
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('uiLibrary.forms.fields.role.label')}
                    <RequiredMark />
                  </FormLabel>
                  <Select required onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger aria-required="true">
                        <SelectValue placeholder={t('uiLibrary.forms.fields.role.placeholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="student">
                        {t('uiLibrary.forms.fields.role.options.student')}
                      </SelectItem>
                      <SelectItem value="instructor">
                        {t('uiLibrary.forms.fields.role.options.instructor')}
                      </SelectItem>
                      <SelectItem value="admin">
                        {t('uiLibrary.forms.fields.role.options.admin')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('uiLibrary.forms.fields.role.helper')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              rules={{
                required: t('uiLibrary.forms.validation.notes'),
                minLength: {
                  value: 10,
                  message: t('uiLibrary.forms.validation.notes'),
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('uiLibrary.forms.fields.notes.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('uiLibrary.forms.fields.notes.placeholder')}
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('uiLibrary.forms.fields.notes.helper')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label htmlFor="example-files">{t('uiLibrary.forms.filePicker.label')}</Label>
              <FilePicker
                id="example-files"
                multiple
                files={files}
                onFilesChange={setFiles}
                chooseLabel={t('uiLibrary.forms.filePicker.choose')}
                changeLabel={t('uiLibrary.forms.filePicker.addMore')}
                helperText={t('uiLibrary.forms.filePicker.helper')}
                previewAlt={t('uiLibrary.forms.filePicker.preview')}
                removeLabel={(file) => t('uiLibrary.forms.filePicker.remove', { name: file.name })}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit">{t('uiLibrary.forms.actions.submit')}</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  setSubmittedName(null);
                  setFiles([]);
                }}
              >
                {t('uiLibrary.forms.actions.reset')}
              </Button>
              {submittedName ? (
                <Text size="sm" color="muted">
                  {t('uiLibrary.forms.actions.success', { name: submittedName })}
                </Text>
              ) : null}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
